import EditIconSmall from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import { isNil } from '@/utilities/format';
import { toCurrencyTruncated } from '@/utilities/formatting';
import type { CurrencyISO } from '@/constants/currencies';
import type { ComponentProps, ReactNode } from 'react';
import { NarrativeAuthorAvatar } from '@/components/NarrativeAuthor';

const NarrativeBtn = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    className="relative grid grid-flow-col items-center gap-px text-accent hover:text-solid focus-visible:ring"
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

type Props = {
  narrativeId?: string;
  currencyISO: CurrencyISO | null;
  fairValue: number | null;
  npvPerShare: number | null;
  onClick: () => void;
  sharePrice: number | null;
  owner:
    | ComponentProps<typeof NarrativeAuthorAvatar>['owner']
    | null
    | undefined;
};

export const PopulatedState = ({
  narrativeId,
  currencyISO,
  fairValue,
  npvPerShare,
  onClick,
  sharePrice,
  owner,
}: Props) => {
  const formattedFairValue = toCurrencyTruncated(
    fairValue || 0,
    (fairValue ?? 0) >= 10 ? 0 : 2,
    currencyISO ?? 'XXX'
  );
  const isFairValueValid = !isNil(fairValue);

  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 text-tiny">
      <div className="mt-[5px] size-1.5 rounded-full bg-chart-03" />
      <div className="grid gap-1">
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div className="whitespace-nowrap font-medium text-soft">
            My Fair Value
          </div>
          {isFairValueValid && (
            <NarrativeBtn onClick={onClick}>
              <span className="truncate">Change narrative</span>
              <EditIconSmall className="mt-px size-[13px] fill-current" />
              {/* Larger hit area */}
              <div className="absolute -inset-2 opacity-0" />
            </NarrativeBtn>
          )}
        </div>
        <>
          {isFairValueValid ? (
            <div className="grid grid-cols-[max-content_auto] items-center gap-x0_5">
              {narrativeId && owner && (
                <div className="-ml-px grid items-center empty:hidden">
                  <NarrativeAuthorAvatar size="xs" owner={owner} />
                </div>
              )}
              <div className="grid grid-cols-[max-content_auto] items-center gap-2">
                <div className="text-xs text-solid">{formattedFairValue}</div>

                <CompanyIntrinsicDiscount
                  fairValue={fairValue}
                  lastSharePrice={sharePrice}
                  npvPerShare={npvPerShare}
                />
              </div>
            </div>
          ) : (
            <div className="text-xs">
              <NarrativeBtn onClick={onClick}>
                <span className="truncate text-left">Select</span>
                <EditIconSmall className="mt-px size-x2 fill-current" />
              </NarrativeBtn>
            </div>
          )}
        </>
      </div>
    </div>
  );
};
